/** Top-bar bell: unread badge + the latest events, "Mark all as read", and "View all notifications". */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { ActivityItem, activityHref, useActivityPreview, useMarkActivity, type ActivityEvent } from "./activity.js";

export function NotificationBell(): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const feed = useActivityPreview(6);
  const mark = useMarkActivity();
  const unread = feed.data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openEvent = (e: ActivityEvent): void => {
    if (!e.read) mark.mutate({ keys: [e.key], read: true });
    setOpen(false);
    const href = activityHref(e);
    if (href) navigate(href);
  };

  return (
    <div className="t1-bell" ref={ref}>
      <button
        type="button"
        className="customer-feedback-btn t1-bell-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : "Notifications"}
        data-tip="Notifications"
      >
        <Bell className="btn-icon" />
        {unread > 0 && <span className="t1-bell-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="t1-bell-panel" role="dialog" aria-label="Recent activity">
          <div className="t1-bell-head">
            <b>Recent activity</b>
            {unread > 0 && (
              <button type="button" className="btn sm ghost" disabled={mark.isPending} onClick={() => mark.mutate({ all: true })}>
                Mark all as read
              </button>
            )}
          </div>
          <div className="t1-bell-list">
            {feed.isLoading && <p className="mut sm t1-bell-empty">Loading…</p>}
            {feed.isError && <p className="mut sm t1-bell-empty">Couldn't load activity.</p>}
            {feed.data && feed.data.events.length === 0 && <p className="mut sm t1-bell-empty">Nothing has happened yet.</p>}
            {feed.data?.events.map((e) => <ActivityItem key={e.key} e={e} onOpen={openEvent} />)}
          </div>
          <Link to="/app/activity" className="t1-bell-all" onClick={() => setOpen(false)}>
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
